import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { FaHome, FaUpload, FaSearch, FaSpinner } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const ImportarOfxScreen = () => {
    const [contas, setContas] = useState([]);
    const [idContaCorrente, setIdContaCorrente] = useState('');
    const [arquivo, setArquivo] = useState(null);
    const [lancamentos, setLancamentos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingContas, setLoadingContas] = useState(false);
    const [loadingBusca, setLoadingBusca] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const URL_BACKEND = process.env.REACT_APP_URL_BACKEND;
    const navigate = useNavigate();

    // 🔹 Buscar todas as contas correntes
    useEffect(() => {
        const fetchContas = async () => {
            setLoadingContas(true);
            try {
                const { data } = await axios.get(`${URL_BACKEND}/contacorrente`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` }
                });

                if (data.sucess) {
                    setContas(data.contas);
                }
            } catch (error) {
                console.error(error);
                toast.error('Erro ao carregar contas correntes.');
            } finally {
                setLoadingContas(false);
            }
        };
        fetchContas();
    }, [URL_BACKEND]);

    // 🔹 Importar arquivo OFX
    const handleImportar = async (e) => {
        e.preventDefault();

        if (!idContaCorrente) {
            toast.error('Selecione uma conta corrente.');
            return;
        }
        if (!arquivo) {
            toast.error('Selecione um arquivo OFX.');
            return;
        }

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('arquivo', arquivo);
            formData.append('idContaCorrente', idContaCorrente);

            const { data } = await axios.post(`${URL_BACKEND}/importarOfx`, formData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('userToken')}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            toast.success(data.message || 'Importação concluída com sucesso!');
            setLancamentos(data.data || []);
            setArquivo(null);
            document.getElementById('input-ofx').value = '';
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.error || 'Erro ao importar arquivo OFX.');
        } finally {
            setLoading(false);
        }
    };

    // 🔹 Buscar lançamentos por período
    const buscarLancamentos = async () => {
        if (!idContaCorrente || !startDate || !endDate) {
            toast.error('Selecione conta e período.');
            return;
        }

        try {
            setLoadingBusca(true);
            const { data } = await axios.get(`${URL_BACKEND}/lancamentosContaCorrente/${idContaCorrente}/${startDate}/${endDate}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` }
            });
            if (Array.isArray(data)) {
                setLancamentos(data);
                toast.success(`Foram encontrados ${data.length} lançamentos.`);
            } else {
                setLancamentos([]);
                toast.error('Nenhum lançamento encontrado.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Erro ao buscar lançamentos.');
        } finally {
            setLoadingBusca(false);
        }
    };

    return (
        <div style={ui.page}>
            {/* Header */}
            <div style={ui.headerBar}>
                <h1 style={ui.headerTitle}>📥 Importar e Conferir Extrato Bancário (.OFX)</h1>
                <button onClick={() => navigate('/dashboard')} style={ui.headerButton}>
                    <FaHome style={{ marginRight: 8 }} /> Voltar
                </button>
            </div>

            {/* Card 1: Importação */}
            <div style={ui.card}>
                <h3 style={ui.sectionTitle}>Importar Arquivo</h3>

                <form onSubmit={handleImportar} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* Conta Corrente */}
                    <div style={ui.formGroup}>
                        <label style={ui.label}>Conta Corrente</label>
                        <select
                            value={idContaCorrente}
                            onChange={(e) => setIdContaCorrente(e.target.value)}
                            style={ui.select}
                            disabled={loadingContas}
                        >
                            {loadingContas ? (
                                <option>Carregando...</option>
                            ) : (
                                <>
                                    <option value="">Selecione...</option>
                                    {contas.map((conta) => (
                                        <option key={conta.id} value={conta.id}>
                                            {/* CORREÇÃO AQUI: USANDO 'conta.Nome' (com 'N' maiúsculo) */}
                                            {conta.Nome || `Conta #${conta.id}`}
                                        </option>
                                    ))}
                                </>
                            )}
                        </select>
                    </div>

                    {/* Arquivo OFX */}
                    <div style={ui.formGroup}>
                        <label style={ui.label}>Arquivo OFX</label>
                        <input
                            id="input-ofx"
                            type="file"
                            accept=".ofx"
                            onChange={(e) => setArquivo(e.target.files[0])}
                            style={ui.input}
                        />
                    </div>

                    {/* Botão Importar */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            type="submit"
                            style={ui.buttonPrimary}
                            disabled={loading || loadingContas}
                        >
                            {loading ? (
                                <>
                                    <FaSpinner className="fa-spin" style={{ marginRight: 8 }} /> Importando...
                                </>
                            ) : (
                                <>
                                    <FaUpload style={{ marginRight: 8 }} /> Enviar Arquivo
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {/* Card 2: Filtros */}
            <div style={ui.card}>
                <h3 style={ui.sectionTitle}>Consultar Lançamentos</h3>

                <div style={ui.filtersRow}>
                    <div style={ui.formGroup}>
                        <label style={ui.label}>Data Inicial</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={ui.input}
                        />
                    </div>
                    <div style={ui.formGroup}>
                        <label style={ui.label}>Data Final</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={ui.input}
                        />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button
                            onClick={buscarLancamentos}
                            disabled={loadingBusca}
                            style={ui.buttonPrimary}
                        >
                            {loadingBusca ? (
                                <>
                                    <FaSpinner className="fa-spin" style={{ marginRight: 8 }} /> Buscando…
                                </>
                            ) : (
                                <>
                                    <FaSearch style={{ marginRight: 8 }} /> Buscar
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Card 3: Resultados */}
            <div style={ui.card}>
                <h3 style={ui.sectionTitle}>Lançamentos Encontrados</h3>
                {lancamentos.length === 0 && (
                    <p style={ui.muted}>Nenhum lançamento encontrado.</p>
                )}

                {lancamentos.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={ui.table}>
                            <thead>
                                <tr>
                                    <th style={ui.th}>Data</th>
                                    <th style={ui.th}>Tipo</th>
                                    <th style={ui.th}>Histórico</th>
                                    <th style={ui.th}>Valor (R$)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lancamentos.map((l) => (
                                    <tr key={l.id} style={ui.tr}>
                                        <td style={ui.td}>
                                            {l.Data
                                                ? format(parseISO(l.Data), 'dd/MM/yyyy')
                                                : '-'}
                                        </td>
                                        <td style={ui.td}>{l.Tipo}</td>
                                        <td
                                            style={{
                                                ...ui.td,
                                                maxWidth: 300,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                            title={l.Historico}
                                        >
                                            {l.Historico}
                                        </td>
                                        <td
                                            style={{
                                                ...ui.td,
                                                color: l.Valor < 0 ? '#dc2626' : '#059669',
                                                fontWeight: 600,
                                            }}
                                        >
                                            {l.Valor.toLocaleString('pt-BR', {
                                                minimumFractionDigits: 2,
                                            })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

// 🔹 Estilos (idênticos ao seu padrão)
const ui = {
    page: { padding: 24, backgroundColor: '#f8fafc', minHeight: '95vh', width: '100%', maxWidth: '1200px', margin: '0 auto', boxSizing: 'border-box', marginBottom: 40 },
    headerBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 24, marginBottom: 24, border: '1px solid #e5e7eb', borderRadius: '16px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', color: '#fff' },
    headerTitle: { margin: 0, fontSize: '1.5rem', fontWeight: 600 },
    headerButton: { backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', padding: '10px 16px', borderRadius: 10, border: '2px solid rgba(255,255,255,0.25)', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center' },
    card: { backgroundColor: '#ffffff', borderRadius: 12, border: '1px solid #e5e7eb', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', padding: 20, marginBottom: 24 },
    sectionTitle: { margin: '0 0 16px 0', fontSize: '1.125rem', fontWeight: 600, color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: 8 },
    filtersRow: { display: 'flex', flexWrap: 'wrap', gap: 16 },
    formGroup: { display: 'flex', flexDirection: 'column', minWidth: 200, flex: 1 },
    label: { marginBottom: 6, fontWeight: 500, color: '#374151', fontSize: '0.875rem' },
    helpText: { fontSize: '0.75rem', color: '#6b7280', marginTop: 4 },
    input: { padding: '12px 16px', borderRadius: 8, border: '2px solid #e5e7eb', fontSize: '0.875rem', transition: 'all 0.2s ease', backgroundColor: '#ffffff', outline: 'none' },
    select: { padding: '12px 16px', borderRadius: 8, border: '2px solid #e5e7eb', fontSize: '0.875rem', backgroundColor: '#ffffff', cursor: 'pointer', outline: 'none', transition: 'all 0.2s ease' },
    buttonPrimary: { backgroundColor: '#667eea', color: '#ffffff', padding: '12px 16px', borderRadius: 8, border: '2px solid #667eea', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem', display: 'flex', alignItems: 'center' },
    table: { width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' },
    th: { backgroundColor: '#e9ecef', padding: 8, textAlign: 'left', color: '#555', fontWeight: 'bold', fontSize: '0.85rem', textTransform: 'uppercase', borderBottom: '2px solid #dee2e6' },
    tr: { backgroundColor: '#fdfdfe', transition: 'background-color 0.2s ease' },
    td: { padding: 8, borderBottom: '1px solid #e9ecef', color: '#495057', fontSize: '0.85rem', verticalAlign: 'middle', lineHeight: 1.3 },
    muted: { color: '#6b7280' }
};

export default ImportarOfxScreen;
